/**
 * Event Planner dialogs — My Branding, Templates, Billing (tasks #618/#619/#621).
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * These four dialogs used to be markup inside `my-projects.html`. That file has
 * six localized siblings (`he/`, `es/`, `fr/`, `de/`, `pt/`, `ar/`) which share
 * `my-projects.js` but each carry their OWN HTML — so a planner working in
 * Hebrew or Spanish had the buttons hidden and the dialogs simply absent. The
 * instruction on task #621 was "everything added for the event planner should be
 * done in all of our languages"; injecting the dialogs from script is what makes
 * that true for all seven pages at once instead of six times over.
 *
 * It also exposes the runtime strings that `my-projects.js` needs when it draws
 * the billing ledger and the branding form:
 *
 *   window.PlannerModals.t('eventTotal')          -> the page's language
 *   window.PlannerModals.chargeLabel(line)        -> "Message credits" / "זיכויי הודעות"
 *   window.PlannerModals.monthLabel(month)        -> "August 2026" / "אוגוסט 2026"
 *
 * Every string falls back to English rather than to a key, and every server
 * value falls back to what the server sent — a label we have not translated yet
 * must still read as something true.
 */
(function () {
  'use strict';

  var LANGS = ['en', 'es', 'fr', 'de', 'pt', 'he', 'ar'];

  // One row per string, in LANGS order. Authored this way rather than as seven
  // separate blocks so a missing or drifted translation is visible on the line
  // it belongs to instead of 200 lines away.
  var S = {
    // ---- shared chrome -----------------------------------------------------
    cancel: ['Cancel', 'Cancelar', 'Annuler', 'Abbrechen', 'Cancelar', 'ביטול', 'إلغاء'],
    close: ['Close', 'Cerrar', 'Fermer', 'Schließen', 'Fechar', 'סגירה', 'إغلاق'],
    save: ['Save', 'Guardar', 'Enregistrer', 'Speichern', 'Salvar', 'שמירה', 'حفظ'],
    loading: ['Loading…', 'Cargando…', 'Chargement…', 'Wird geladen…', 'Carregando…', 'טוען…', 'جارٍ التحميل…'],

    // ---- template library (#619) -------------------------------------------
    tlTitle: ['Start from a template', 'Empezar desde una plantilla', 'Partir d\'un modèle',
      'Mit einer Vorlage starten', 'Começar de um modelo', 'התחלה מתבנית', 'ابدأ من قالب'],
    tlNameLabel: ['What is this event called?', '¿Cómo se llama este evento?', 'Comment s\'appelle cet événement ?',
      'Wie heißt dieses Event?', 'Como se chama este evento?', 'איך קוראים לאירוע הזה?', 'ما اسم هذه الفعالية؟'],
    tlNamePlaceholder: ['Sarah & David\'s Wedding', 'La boda de Sara y David', 'Le mariage de Sarah et David',
      'Die Hochzeit von Sarah und David', 'O casamento de Sara e David', 'החתונה של שרה ודוד', 'زفاف سارة وداود'],
    tlHelp: ['The messages come across as drafts with no guests on them — add the client\'s list and send.',
      'Los mensajes llegan como borradores sin invitados — añade la lista del cliente y envía.',
      'Les messages arrivent en brouillon, sans invités — ajoutez la liste du client et envoyez.',
      'Die Nachrichten kommen als Entwürfe ohne Gäste an — füge die Liste des Kunden hinzu und sende.',
      'As mensagens chegam como rascunhos sem convidados — adicione a lista do cliente e envie.',
      'ההודעות מגיעות כטיוטות בלי מוזמנים — הוסיפו את הרשימה של הלקוח ושלחו.',
      'تصل الرسائل كمسودات بلا مدعوين — أضف قائمة العميل وأرسل.'],
    tlCreate: ['Create event', 'Crear evento', 'Créer l\'événement', 'Event erstellen', 'Criar evento',
      'יצירת אירוע', 'إنشاء الفعالية'],

    // ---- save as template (#619) -------------------------------------------
    // One dialog does save / rename / create, and it used to announce itself
    // as "Save as a template" in all three -- which on a RENAME of something
    // already saved reads as a redundant second save (task #703).
    tsTitleRename: ['Rename this template', 'Renombrar esta plantilla', 'Renommer ce modèle',
      'Diese Vorlage umbenennen', 'Renomear este modelo', 'שינוי שם התבנית', 'إعادة تسمية هذا القالب'],
    tsTitleCreate: ['New template', 'Nueva plantilla', 'Nouveau modèle',
      'Neue Vorlage', 'Novo modelo', 'תבנית חדשה', 'قالب جديد'],
    tsSaveRename: ['Rename', 'Renombrar', 'Renommer', 'Umbenennen', 'Renomear', 'שינוי שם', 'إعادة تسمية'],
    tsSaveCreate: ['Create', 'Crear', 'Créer', 'Erstellen', 'Criar', 'יצירה', 'إنشاء'],
    tsTitle: ['Save as a template', 'Guardar como plantilla', 'Enregistrer comme modèle',
      'Als Vorlage speichern', 'Salvar como modelo', 'שמירה כתבנית', 'الحفظ كقالب'],
    tsLeadPre: ['Saves everything about', 'Guarda todo lo de', 'Enregistre tout ce qui concerne',
      'Speichert alles zu', 'Salva tudo sobre', 'שומר את כל מה שקשור ל', 'يحفظ كل ما يخص'],
    tsLeadPost: ['— its questions and setup, and its messages with their reminder schedule — so you can start the next client from it. The guest list is never part of a template.',
      '— sus preguntas y su configuración, y sus mensajes con su calendario de recordatorios — para empezar el siguiente cliente desde ahí. La lista de invitados nunca forma parte de una plantilla.',
      '— ses questions et sa configuration, et ses messages avec leur calendrier de rappels — pour démarrer le client suivant à partir de là. La liste des invités ne fait jamais partie d\'un modèle.',
      '— seine Fragen und Einstellungen und seine Nachrichten samt Erinnerungsplan — damit du den nächsten Kunden damit beginnen kannst. Die Gästeliste ist nie Teil einer Vorlage.',
      '— suas perguntas e configurações, e suas mensagens com o cronograma de lembretes — para começar o próximo cliente a partir dele. A lista de convidados nunca faz parte de um modelo.',
      '— השאלות וההגדרות שלו, וההודעות שלו עם לוח התזכורות — כדי להתחיל מהן את הלקוח הבא. רשימת המוזמנים אף פעם לא חלק מתבנית.',
      '— أسئلتها وإعداداتها، ورسائلها مع جدول التذكيرات — لتبدأ منها مع العميل التالي. قائمة المدعوين ليست جزءاً من القالب أبداً.'],
    tsThisEvent: ['this event', 'este evento', 'cet événement', 'dieses Event', 'este evento',
      'האירוע הזה', 'هذه الفعالية'],
    tsNameLabel: ['Template name', 'Nombre de la plantilla', 'Nom du modèle', 'Name der Vorlage',
      'Nome do modelo', 'שם התבנית', 'اسم القالب'],
    tsNamePlaceholder: ['Wedding Standard', 'Boda estándar', 'Mariage standard', 'Hochzeit Standard',
      'Casamento padrão', 'חתונה סטנדרטית', 'زفاف قياسي'],
    tsNoteLabel: ['Note (optional)', 'Nota (opcional)', 'Note (facultatif)', 'Notiz (optional)',
      'Nota (opcional)', 'הערה (לא חובה)', 'ملاحظة (اختياري)'],
    // Task #831: no languages in the example - translating happens when an
    // event is made from the template (#797), not in the template itself.
    tsNotePlaceholder: ['e.g. Two reminders, meal choice, for outdoor weddings',
      'p. ej. Dos recordatorios, elección de menú, para bodas al aire libre',
      'ex. Deux rappels, choix du repas, pour les mariages en plein air',
      'z. B. Zwei Erinnerungen, Menüwahl, für Hochzeiten im Freien',
      'ex.: Dois lembretes, escolha de prato, para casamentos ao ar livre',
      'למשל: שתי תזכורות, בחירת מנה, לחתונות בחוץ',
      'مثال: تذكيران، اختيار الوجبة، لحفلات الزفاف في الهواء الطلق'],
    tsRemove: ['Remove from templates', 'Quitar de las plantillas', 'Retirer des modèles',
      'Aus den Vorlagen entfernen', 'Remover dos modelos', 'הסרה מהתבניות', 'إزالة من القوالب'],
    tsSave: ['Save template', 'Guardar plantilla', 'Enregistrer le modèle', 'Vorlage speichern',
      'Salvar modelo', 'שמירת תבנית', 'حفظ القالب'],

    // ---- billing (#621) ----------------------------------------------------
    billingTitle: ['Billing', 'Facturación', 'Facturation', 'Abrechnung', 'Faturamento',
      'חיובים', 'الفواتير'],
    downloadCsv: ['Download CSV', 'Descargar CSV', 'Télécharger le CSV', 'CSV herunterladen',
      'Baixar CSV', 'הורדת CSV', 'تنزيل CSV'],
    billingSubscription: ['Event Planner subscription', 'Suscripción Event Planner',
      'Abonnement Event Planner', 'Event-Planner-Abo', 'Assinatura Event Planner',
      'מנוי Event Planner', 'اشتراك Event Planner'],
    statusActive: ['Active', 'Activa', 'Actif', 'Aktiv', 'Ativa', 'פעיל', 'نشط'],
    billingEmpty: ['No charges yet. Every event you upgrade will appear here, with the couple\'s name beside it.',
      'Aún no hay cargos. Cada evento que mejores aparecerá aquí, con el nombre de la pareja al lado.',
      'Aucun frais pour le moment. Chaque événement que vous améliorez apparaîtra ici, avec le nom du couple à côté.',
      'Noch keine Buchungen. Jedes Event, das du upgradest, erscheint hier — mit dem Namen des Paares daneben.',
      'Ainda não há cobranças. Cada evento que você aprimorar aparecerá aqui, com o nome do casal ao lado.',
      'עדיין אין חיובים. כל אירוע שתשדרגו יופיע כאן, עם שם בני הזוג לצידו.',
      'لا توجد رسوم بعد. كل فعالية تقوم بترقيتها ستظهر هنا، مع اسم العروسين بجانبها.'],
    billingTruncated: ['Showing your most recent charges only. Message us via the chat bubble if you need the full history.',
      'Mostrando solo tus cargos más recientes. Escríbenos por el chat si necesitas el historial completo.',
      'Seuls vos frais les plus récents sont affichés. Écrivez-nous via la bulle de chat s\'il vous faut l\'historique complet.',
      'Es werden nur deine neuesten Buchungen gezeigt. Schreib uns über die Chat-Blase, wenn du die vollständige Historie brauchst.',
      'Mostrando apenas suas cobranças mais recentes. Fale conosco pelo chat se precisar do histórico completo.',
      'מוצגים רק החיובים האחרונים שלכם. כתבו לנו בצ\'אט אם אתם צריכים את ההיסטוריה המלאה.',
      'تُعرض أحدث رسومك فقط. راسلنا عبر فقاعة الدردشة إذا احتجت السجل الكامل.'],
    billingYourSubscription: ['Your subscription', 'Tu suscripción', 'Votre abonnement', 'Dein Abo',
      'Sua assinatura', 'המנוי שלכם', 'اشتراكك'],
    billingAccountWide: ['Account-wide', 'De la cuenta', 'Sur le compte', 'Kontoweit', 'Da conta',
      'ברמת החשבון', 'على مستوى الحساب'],
    billingMixed: ['This month mixes currencies, so the lines are listed without a single total.',
      'Este mes mezcla monedas, así que las líneas se listan sin un total único.',
      'Ce mois-ci mélange plusieurs devises : les lignes sont listées sans total unique.',
      'Dieser Monat mischt Währungen, deshalb stehen die Zeilen ohne eine Gesamtsumme.',
      'Este mês mistura moedas, então as linhas são listadas sem um total único.',
      'החודש הזה מערבב מטבעות, ולכן השורות מוצגות בלי סכום כולל אחד.',
      'يخلط هذا الشهر بين عملات، لذلك تُعرض البنود دون مجموع واحد.'],
    billingEventTotal: ['Event total', 'Total de eventos', 'Total événements', 'Event-Summe',
      'Total de eventos', 'סה"כ אירועים', 'إجمالي الفعاليات'],
    billingPlannerSub: ['Planner subscription', 'Suscripción de organizador', 'Abonnement organisateur',
      'Planer-Abo', 'Assinatura de organizador', 'מנוי מארגן', 'اشتراك المنظّم'],
    billingTotal: ['Total', 'Total', 'Total', 'Gesamt', 'Total', 'סה"כ', 'الإجمالي'],
    billingError: ['Could not load your billing.', 'No se pudo cargar tu facturación.',
      'Impossible de charger votre facturation.', 'Abrechnung konnte nicht geladen werden.',
      'Não foi possível carregar seu faturamento.', 'לא הצלחנו לטעון את החיובים שלכם.',
      'تعذّر تحميل فواتيرك.'],
    csvMonth: ['Month', 'Mes', 'Mois', 'Monat', 'Mês', 'חודש', 'الشهر'],
    csvDate: ['Date', 'Fecha', 'Date', 'Datum', 'Data', 'תאריך', 'التاريخ'],
    csvEvent: ['Event', 'Evento', 'Événement', 'Event', 'Evento', 'אירוע', 'الفعالية'],
    csvCharge: ['Charge', 'Cargo', 'Frais', 'Buchung', 'Cobrança', 'חיוב', 'الرسم'],
    csvAmount: ['Amount', 'Importe', 'Montant', 'Betrag', 'Valor', 'סכום', 'المبلغ'],
    csvCurrency: ['Currency', 'Moneda', 'Devise', 'Währung', 'Moeda', 'מטבע', 'العملة'],

    // ---- branding (#618) ---------------------------------------------------
    brandingTitle: ['My branding', 'Mi marca', 'Ma marque', 'Meine Marke', 'Minha marca',
      'המותג שלי', 'علامتي التجارية'],
    brandingTitleEvent: ['Branding for {event}', 'Marca para {event}', 'Marque pour {event}',
      'Branding für {event}', 'Marca para {event}', 'מיתוג עבור {event}', 'العلامة التجارية لـ {event}'],
    brandingLead: ['Set this up once and it appears on the RSVP form and event website of every event you run, above “Powered by QuikRSVP”.',
      'Configúralo una vez y aparecerá en el formulario RSVP y en el sitio de cada evento que organices, encima de “Powered by QuikRSVP”.',
      'Configurez-le une fois : il apparaîtra sur le formulaire RSVP et le site de chaque événement que vous organisez, au-dessus de « Powered by QuikRSVP ».',
      'Einmal einrichten — es erscheint auf dem RSVP-Formular und der Event-Website jedes Events, das du organisierst, über „Powered by QuikRSVP“.',
      'Configure uma vez e aparecerá no formulário RSVP e no site de cada evento que você organiza, acima de “Powered by QuikRSVP”.',
      'מגדירים פעם אחת וזה מופיע בטופס ה-RSVP ובאתר של כל אירוע שאתם מפיקים, מעל “Powered by QuikRSVP”.',
      'اضبطها مرة واحدة لتظهر في نموذج التأكيد وموقع كل فعالية تنظّمها، فوق “Powered by QuikRSVP”.'],
    brandingLeadEvent: ['Choose what this one event shows. Everything else keeps your default branding.',
      'Elige lo que muestra este evento. Todo lo demás mantiene tu marca por defecto.',
      'Choisissez ce que cet événement affiche. Tout le reste garde votre marque par défaut.',
      'Wähle, was dieses eine Event zeigt. Alles andere behält deine Standardmarke.',
      'Escolha o que este evento mostra. Todo o resto mantém sua marca padrão.',
      'בחרו מה האירוע הזה מציג. כל השאר נשאר עם המיתוג שהגדרתם.',
      'اختر ما تعرضه هذه الفعالية. كل ما عداها يبقى على علامتك الافتراضية.'],
    modeDefault: ['Use my default planner branding', 'Usar mi marca de organizador por defecto',
      'Utiliser ma marque d\'organisateur par défaut', 'Meine Standard-Planermarke verwenden',
      'Usar minha marca de organizador padrão', 'להשתמש במיתוג ברירת המחדל שלי',
      'استخدام علامتي الافتراضية كمنظّم'],
    modeCustom: ['Use custom branding for this event', 'Usar una marca propia para este evento',
      'Utiliser une marque spécifique pour cet événement', 'Eigenes Branding für dieses Event verwenden',
      'Usar uma marca própria para este evento', 'להשתמש במיתוג נפרד לאירוע הזה',
      'استخدام علامة مخصصة لهذه الفعالية'],
    modeNone: ['No planner branding on this event', 'Sin marca de organizador en este evento',
      'Aucune marque d\'organisateur sur cet événement', 'Kein Planer-Branding auf diesem Event',
      'Sem marca de organizador neste evento', 'בלי מיתוג מארגן באירוע הזה',
      'بدون علامة المنظّم في هذه الفعالية'],
    fCompanyName: ['Company name', 'Nombre de la empresa', 'Nom de l\'entreprise', 'Firmenname',
      'Nome da empresa', 'שם העסק', 'اسم الشركة'],
    fCompanyPlaceholder: ['Rose & Co. Events', 'Rose & Co. Eventos', 'Rose & Co. Événements',
      'Rose & Co. Events', 'Rose & Co. Eventos', 'רוז אירועים', 'روز للفعاليات'],
    fLogo: ['Logo', 'Logo', 'Logo', 'Logo', 'Logo', 'לוגו', 'الشعار'],
    fUpload: ['Upload', 'Subir', 'Téléverser', 'Hochladen', 'Enviar', 'העלאה', 'رفع'],
    fRemove: ['Remove', 'Quitar', 'Retirer', 'Entfernen', 'Remover', 'הסרה', 'إزالة'],
    fWebsite: ['Website', 'Sitio web', 'Site web', 'Website', 'Site', 'אתר', 'الموقع'],
    fInstagram: ['Instagram', 'Instagram', 'Instagram', 'Instagram', 'Instagram', 'אינסטגרם', 'إنستغرام'],
    fContactEmail: ['Contact email', 'Email de contacto', 'E-mail de contact', 'Kontakt-E-Mail',
      'E-mail de contato', 'אימייל ליצירת קשר', 'البريد للتواصل'],
    fPhone: ['Phone', 'Teléfono', 'Téléphone', 'Telefon', 'Telefone', 'טלפון', 'الهاتف'],
    fDescription: ['Short description', 'Descripción breve', 'Description courte', 'Kurzbeschreibung',
      'Descrição curta', 'תיאור קצר', 'وصف قصير'],
    fDescriptionPlaceholder: ['Weddings and celebrations across the Northeast',
      'Bodas y celebraciones en toda la región', 'Mariages et célébrations dans toute la région',
      'Hochzeiten und Feiern in der ganzen Region', 'Casamentos e celebrações em toda a região',
      'חתונות ואירועים בכל הארץ', 'أعراس ومناسبات في كل المنطقة'],
    brandingFootnote: ['“Powered by QuikRSVP” stays under your name on guest pages.',
      '“Powered by QuikRSVP” permanece debajo de tu nombre en las páginas de invitados.',
      '« Powered by QuikRSVP » reste sous votre nom sur les pages destinées aux invités.',
      '„Powered by QuikRSVP“ bleibt auf den Gästeseiten unter deinem Namen stehen.',
      '“Powered by QuikRSVP” permanece abaixo do seu nome nas páginas dos convidados.',
      '“Powered by QuikRSVP” נשאר מתחת לשם שלכם בעמודים שהאורחים רואים.',
      'تبقى عبارة “Powered by QuikRSVP” أسفل اسمك في صفحات المدعوين.'],

    // ---- charge descriptions, keyed to the server's labelKey ----------------
    'charge.plan.basic': ['Event Pro', 'Event Pro', 'Event Pro', 'Event Pro', 'Event Pro', 'Event Pro', 'Event Pro'],
    'charge.plan.basic_signature': ['Event Pro Signature', 'Event Pro Signature', 'Event Pro Signature',
      'Event Pro Signature', 'Event Pro Signature', 'Event Pro Signature', 'Event Pro Signature'],
    'charge.plan.premium': ['Event Pro Signature', 'Event Pro Signature', 'Event Pro Signature',
      'Event Pro Signature', 'Event Pro Signature', 'Event Pro Signature', 'Event Pro Signature'],
    'charge.plan.corporate': ['Corporate', 'Corporate', 'Corporate', 'Corporate', 'Corporate', 'Corporate', 'Corporate'],
    'charge.plan.planner': ['Event Planner subscription', 'Suscripción Event Planner',
      'Abonnement Event Planner', 'Event-Planner-Abo', 'Assinatura Event Planner',
      'מנוי Event Planner', 'اشتراك Event Planner'],
    'charge.product.planner_subscription': ['Event Planner subscription', 'Suscripción Event Planner',
      'Abonnement Event Planner', 'Event-Planner-Abo', 'Assinatura Event Planner',
      'מנוי Event Planner', 'اشتراك Event Planner'],
    'charge.product.form_upgrade': ['Event upgrade', 'Mejora de evento', 'Amélioration d\'événement',
      'Event-Upgrade', 'Upgrade de evento', 'שדרוג אירוע', 'ترقية فعالية'],
    'charge.product.signature_upgrade': ['Signature upgrade', 'Mejora a Signature', 'Passage à Signature',
      'Signature-Upgrade', 'Upgrade para Signature', 'שדרוג ל-Signature', 'الترقية إلى Signature'],
    'charge.product.credit_purchase': ['Message credits', 'Créditos de mensajes', 'Crédits de messages',
      'Nachrichten-Guthaben', 'Créditos de mensagens', 'זיכויי הודעות', 'أرصدة الرسائل'],
    'charge.product.credits_with_eventpro': ['Event upgrade + credits', 'Mejora de evento + créditos',
      'Amélioration d\'événement + crédits', 'Event-Upgrade + Guthaben', 'Upgrade de evento + créditos',
      'שדרוג אירוע + זיכויים', 'ترقية فعالية + أرصدة'],
    'charge.product.wedding_website_publish': ['Wedding website', 'Sitio web de boda', 'Site de mariage',
      'Hochzeitswebsite', 'Site de casamento', 'אתר חתונה', 'موقع زفاف'],
    'charge.product.photo_album': ['Photo album', 'Álbum de fotos', 'Album photo', 'Fotoalbum',
      'Álbum de fotos', 'אלבום תמונות', 'ألبوم صور'],
    'charge.product.event_tickets_tiered': ['Ticket sales', 'Venta de entradas', 'Vente de billets',
      'Ticketverkauf', 'Venda de ingressos', 'מכירת כרטיסים', 'مبيعات التذاكر'],
    'charge.product.registry_contribution': ['Registry contribution', 'Aportación a la lista de regalos',
      'Contribution à la liste de cadeaux', 'Beitrag zur Geschenkeliste', 'Contribuição para a lista de presentes',
      'תרומה לרשימת המתנות', 'مساهمة في قائمة الهدايا'],
    'charge.product.donation': ['Donation', 'Donación', 'Don', 'Spende', 'Doação', 'תרומה', 'تبرع'],
    'charge.product.donation_recurring': ['Donation', 'Donación', 'Don', 'Spende', 'Doação', 'תרומה', 'تبرع'],
    'charge.product.application_fee': ['Platform fee', 'Comisión de la plataforma', 'Frais de plateforme',
      'Plattformgebühr', 'Taxa da plataforma', 'עמלת פלטפורמה', 'رسوم المنصة'],
    'charge.product.dispute': ['Disputed charge', 'Cargo disputado', 'Frais contestés',
      'Angefochtene Buchung', 'Cobrança contestada', 'חיוב שנוי במחלוקת', 'رسم متنازع عليه'],
    'charge.product.refund': ['Refund', 'Reembolso', 'Remboursement', 'Erstattung', 'Reembolso',
      'החזר', 'استرداد'],
    'charge.product.refund_void': ['Refund reversed', 'Reembolso revertido', 'Remboursement annulé',
      'Erstattung zurückgenommen', 'Reembolso revertido', 'ההחזר בוטל', 'تم إلغاء الاسترداد'],
    'charge.refund': ['Refund', 'Reembolso', 'Remboursement', 'Erstattung', 'Reembolso', 'החזר', 'استرداد'],
    'charge.dispute': ['Disputed', 'Disputado', 'Contesté', 'Angefochten', 'Contestado',
      'שנוי במחלוקת', 'متنازع عليه'],
    'charge.charge': ['Charge', 'Cargo', 'Frais', 'Buchung', 'Cobrança', 'חיוב', 'رسم']
  };

  function lang() {
    var l = (document.documentElement.getAttribute('lang') || 'en').slice(0, 2).toLowerCase();
    return LANGS.indexOf(l) === -1 ? 'en' : l;
  }

  /** The string in the page's language, or English, or the key. Never blank. */
  function t(key) {
    var row = S[key];
    if (!row) return key;
    return row[LANGS.indexOf(lang())] || row[0] || key;
  }

  function isRtl() {
    return (document.documentElement.getAttribute('dir') || '').toLowerCase() === 'rtl';
  }

  /** Bootstrap's margin utilities are physical here, so pick the right side. */
  function ms() { return isRtl() ? 'ms-2' : 'me-2'; }
  function ms1() { return isRtl() ? 'ms-1' : 'me-1'; }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /**
   * A charge's description in the reader's language.
   *
   * The server sends a stable `labelKey` beside the English `label`. A key we do
   * not know — a product type added after this file was written — falls back to
   * the server's own words, which are at least correct.
   *
   * `refund.signature_upgrade` composes: "Refund" + what was refunded.
   */
  function chargeLabel(line) {
    if (!line) return '';
    var key = line.labelKey;
    if (!key) return line.label || '';
    if (S['charge.' + key]) return t('charge.' + key);
    var parts = String(key).split('.');
    if ((parts[0] === 'refund' || parts[0] === 'dispute') && parts[1]) {
      var head = t('charge.' + parts[0]);
      var what = S['charge.product.' + parts[1]] ? t('charge.product.' + parts[1]) : '';
      return what ? head + ' — ' + what : head;
    }
    return line.label || key;
  }

  /**
   * "August 2026" in the reader's language, from the month's `key` ("2026-08").
   * The server's English `label` is the fallback, so a browser without Intl or
   * a month with no key still gets a heading.
   */
  function monthLabel(month) {
    if (!month) return '';
    var key = String(month.key || '');
    var m = /^(\d{4})-(\d{2})$/.exec(key);
    if (!m) return month.label || '';
    try {
      return new Intl.DateTimeFormat(lang(), { month: 'long', year: 'numeric' })
        .format(new Date(Number(m[1]), Number(m[2]) - 1, 1));
    } catch (e) {
      return month.label || key;
    }
  }

  /** The four dialogs, as one block of markup appended to the page. */
  function markup() {
    var X = ms(), X1 = ms1();
    return ''
      // --- Templates: start from one -------------------------------------
      + '<div class="modal fade" id="plannerTemplateLibraryModal" tabindex="-1" aria-hidden="true">'
      + '<div class="modal-dialog modal-dialog-centered modal-dialog-scrollable"><div class="modal-content">'
      + '<div class="modal-header"><h5 class="modal-title"><i class="bi bi-bookmark ' + X + '"></i>' + esc(t('tlTitle')) + '</h5>'
      + '<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="' + esc(t('close')) + '"></button></div>'
      + '<div class="modal-body"><div id="ptLibraryBody"><div class="text-center py-4">'
      + '<div class="spinner-border text-primary" role="status"><span class="visually-hidden">' + esc(t('loading')) + '</span></div>'
      + '</div></div>'
      // Task #704: the name box and the button that acts on it used to be at
      // opposite ends of the dialog — the box in the body, "Create event" down
      // in the footer past a scroll, where it read as a general dialog action
      // rather than as the other half of the sentence "call it ___ and go".
      // They are one control now. The footer keeps Cancel alone, which is also
      // what lets the studio's adopted panel end without an empty button bar.
      + '<div id="ptCreateRow" class="d-none mt-3">'
      // Task #811: no "What is this event called?" box here any more - the
      // event is named by the template's own title tag, or by an "Event name"
      // row the fill-in step adds when the title has no tag. Task #812: a
      // Cancel beside Create, so the flow can be left at any point.
      // Task #824: the helper line sits on the left of the buttons, on the
      // same row, and only for a template that carries messages (my-projects
      // selectTemplate toggles .pt-has-messages). Cancel is hidden while the
      // row is inside the picked card - clicking the card again lets go of it
      // - and stays on the fill-in step, where it is the only way back.
      + '<div class="pt-create-bar">'
      + '<div class="form-text pt-create-help">' + esc(t('tlHelp')) + '</div>'
      + '<div class="d-flex gap-2 align-items-center flex-shrink-0 ms-auto">'
      + '<button type="button" class="btn btn-link btn-sm text-secondary" id="ptCancelCreate" onclick="window.myProjects?.cancelTemplateCreate()">' + esc(t('cancel')) + '</button>'
      + '<button type="button" class="btn btn-primary btn-sm flex-shrink-0" id="ptCreateBtn" disabled onclick="window.myProjects?.useTemplate()">' + esc(t('tlCreate')) + '</button>'
      + '</div></div></div></div>'
      + '<div class="modal-footer">'
      + '<button type="button" class="btn btn-secondary btn-sm" data-bs-dismiss="modal">' + esc(t('cancel')) + '</button>'
      + '</div></div></div></div>'

      // --- Templates: save one -------------------------------------------
      + '<div class="modal fade" id="plannerTemplateSaveModal" tabindex="-1" aria-hidden="true">'
      + '<div class="modal-dialog modal-dialog-centered"><div class="modal-content">'
      + '<div class="modal-header"><h5 class="modal-title" id="ptSaveTitle"><i class="bi bi-bookmark-plus ' + X + '"></i>' + esc(t('tsTitle')) + '</h5>'
      + '<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="' + esc(t('close')) + '"></button></div>'
      + '<div class="modal-body"><p class="text-muted small">' + esc(t('tsLeadPre'))
      + ' <strong id="ptSaveEventName">' + esc(t('tsThisEvent')) + '</strong> ' + esc(t('tsLeadPost')) + '</p>'
      + '<div class="mb-2"><label class="form-label small" for="ptSaveName">' + esc(t('tsNameLabel')) + '</label>'
      + '<input type="text" class="form-control form-control-sm" id="ptSaveName" placeholder="' + esc(t('tsNamePlaceholder')) + '" maxlength="80"></div>'
      + '<div class="mb-2"><label class="form-label small" for="ptSaveDesc">' + esc(t('tsNoteLabel')) + '</label>'
      + '<input type="text" class="form-control form-control-sm" id="ptSaveDesc" placeholder="' + esc(t('tsNotePlaceholder')) + '" maxlength="300"></div>'
      + '<div class="alert alert-danger py-2 mb-0" id="ptSaveError" style="display:none;font-size:0.85rem;"></div></div>'
      + '<div class="modal-footer">'
      + '<button type="button" class="btn btn-outline-danger btn-sm d-none" id="ptSaveRemove" onclick="window.myProjects?.savePlannerTemplate(true)">' + esc(t('tsRemove')) + '</button>'
      + '<button type="button" class="btn btn-secondary btn-sm" data-bs-dismiss="modal">' + esc(t('cancel')) + '</button>'
      + '<button type="button" class="btn btn-primary btn-sm" onclick="window.myProjects?.savePlannerTemplate(false)">' + esc(t('tsSave')) + '</button>'
      + '</div></div></div></div>'

      // --- Billing ledger -------------------------------------------------
      + '<div class="modal fade" id="plannerBillingModal" tabindex="-1" aria-hidden="true">'
      + '<div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg"><div class="modal-content">'
      + '<div class="modal-header"><h5 class="modal-title"><i class="bi bi-receipt ' + X + '"></i>' + esc(t('billingTitle')) + '</h5>'
      + '<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="' + esc(t('close')) + '"></button></div>'
      + '<div class="modal-body"><div id="plannerBillingBody"><div class="text-center py-4">'
      + '<div class="spinner-border text-primary" role="status"><span class="visually-hidden">' + esc(t('loading')) + '</span></div>'
      + '</div></div></div>'
      + '<div class="modal-footer">'
      + '<button type="button" class="btn btn-outline-secondary btn-sm" id="plannerBillingCsvBtn"><i class="bi bi-download ' + X1 + '"></i>' + esc(t('downloadCsv')) + '</button>'
      + '<button type="button" class="btn btn-secondary btn-sm" data-bs-dismiss="modal">' + esc(t('close')) + '</button>'
      + '</div></div></div></div>'

      // --- Branding --------------------------------------------------------
      + '<div class="modal fade" id="plannerBrandingModal" tabindex="-1" aria-hidden="true">'
      + '<div class="modal-dialog modal-dialog-centered modal-dialog-scrollable"><div class="modal-content">'
      + '<div class="modal-header"><h5 class="modal-title" id="plannerBrandingTitle"><i class="bi bi-palette ' + X + '"></i>' + esc(t('brandingTitle')) + '</h5>'
      + '<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="' + esc(t('close')) + '"></button></div>'
      + '<div class="modal-body">'
      // Task #634: the error banner is the FIRST thing in the body, not the
      // last — an upload failure reported below the fold looked like a button
      // that simply did nothing.
      + '<div class="alert alert-danger py-2 px-3 small d-none" id="pbError" role="alert" aria-live="assertive"></div>'
      + '<p class="text-muted small" id="plannerBrandingLead">' + esc(t('brandingLead')) + '</p>'
      + '<div id="plannerBrandingModeBlock" class="mb-3 d-none">'
      + '<div class="form-check"><input class="form-check-input" type="radio" name="plannerBrandingMode" id="pbModeDefault" value="default" checked>'
      + '<label class="form-check-label" for="pbModeDefault">' + esc(t('modeDefault')) + '</label></div>'
      + '<div class="form-check"><input class="form-check-input" type="radio" name="plannerBrandingMode" id="pbModeCustom" value="custom">'
      + '<label class="form-check-label" for="pbModeCustom">' + esc(t('modeCustom')) + '</label></div>'
      + '<div class="form-check"><input class="form-check-input" type="radio" name="plannerBrandingMode" id="pbModeNone" value="none">'
      + '<label class="form-check-label" for="pbModeNone">' + esc(t('modeNone')) + '</label></div>'
      + '</div>'
      + '<div id="plannerBrandingFields">'
      + '<div class="mb-3"><label class="form-label small fw-semibold" for="pbCompanyName">' + esc(t('fCompanyName')) + '</label>'
      + '<input type="text" class="form-control form-control-sm" id="pbCompanyName" maxlength="80" placeholder="' + esc(t('fCompanyPlaceholder')) + '"></div>'
      + '<div class="mb-3"><label class="form-label small fw-semibold">' + esc(t('fLogo')) + '</label>'
      + '<div class="d-flex align-items-center gap-2">'
      + '<img id="pbLogoPreview" alt="" class="d-none" style="height:40px;width:auto;max-width:120px;object-fit:contain;border:1px solid #e5e7eb;border-radius:4px;">'
      + '<button type="button" class="btn btn-sm btn-outline-secondary" id="pbLogoUploadBtn"><i class="bi bi-upload ' + X1 + '"></i>' + esc(t('fUpload')) + '</button>'
      + '<button type="button" class="btn btn-sm btn-outline-secondary d-none" id="pbLogoRemoveBtn">' + esc(t('fRemove')) + '</button>'
      + '<input type="file" id="pbLogoFile" accept="image/png,image/jpeg,image/webp" class="d-none"></div>'
      + '<input type="hidden" id="pbLogoUrl"></div>'
      + '<div class="row g-2">'
      + '<div class="col-sm-6 mb-3"><label class="form-label small fw-semibold" for="pbWebsite">' + esc(t('fWebsite')) + '</label>'
      + '<input type="url" class="form-control form-control-sm" id="pbWebsite" placeholder="roseandco.com" dir="ltr"></div>'
      + '<div class="col-sm-6 mb-3"><label class="form-label small fw-semibold" for="pbInstagram">' + esc(t('fInstagram')) + '</label>'
      + '<input type="text" class="form-control form-control-sm" id="pbInstagram" placeholder="@roseandco" dir="ltr"></div>'
      + '<div class="col-sm-6 mb-3"><label class="form-label small fw-semibold" for="pbContactEmail">' + esc(t('fContactEmail')) + '</label>'
      + '<input type="email" class="form-control form-control-sm" id="pbContactEmail" placeholder="hello@roseandco.com" dir="ltr"></div>'
      + '<div class="col-sm-6 mb-3"><label class="form-label small fw-semibold" for="pbPhone">' + esc(t('fPhone')) + '</label>'
      + '<input type="tel" class="form-control form-control-sm" id="pbPhone" maxlength="40" dir="ltr"></div>'
      + '</div>'
      + '<div class="mb-2"><label class="form-label small fw-semibold" for="pbDescription">' + esc(t('fDescription')) + '</label>'
      + '<input type="text" class="form-control form-control-sm" id="pbDescription" maxlength="200" placeholder="' + esc(t('fDescriptionPlaceholder')) + '"></div>'
      + '</div>'
      + '<p class="text-muted small mb-0" id="pbFootnote"><i class="bi bi-info-circle ' + X1 + '"></i>' + esc(t('brandingFootnote')) + '</p>'
      + '</div>'
      + '<div class="modal-footer">'
      + '<button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">' + esc(t('cancel')) + '</button>'
      + '<button type="button" class="btn btn-primary" id="pbSaveBtn">' + esc(t('save')) + '</button>'
      + '</div></div></div></div>';
  }

  /**
   * Append the dialogs once. Safe to call twice, and safe to call on a page
   * that has nothing to do with planners — the markup is inert until something
   * opens it, and Bootstrap only reads it when a modal is shown.
   */
  /**
   * How a selected template looks (task #741).
   *
   * It used to be Bootstrap's `.active`, which is a solid electric blue panel
   * with white text — a colour that appears nowhere else in this product, on
   * the one row you are meant to be reading. A selection is not an alert; it
   * only has to be unmistakable, and a warm tint with a gold spine on the
   * leading edge is unmistakable while still letting you read the name.
   *
   * Written here rather than in a stylesheet because this dialog is injected by
   * this file onto eight pages (my-projects in seven languages, plus the
   * planner studio) and the alternative is eight <link> tags to keep in step.
   *
   * `border-inline-start` and `padding-inline-start`, not `left`: the same
   * dialog runs in Hebrew and Arabic, where the leading edge is the right one.
   */
  var STYLE_ID = 'plannerTemplateStyles';
  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var css = [
      /* Task #787: a FULL border on every card. Bootstrap's list-group drops
         the top border of every item after the first (it expects a joined
         stack, not separate cards), and the leading edge used to be a 3px
         TRANSPARENT border reserved for the selected spine - so each card
         showed no top and no left edge. Keyed off #ptLibraryBody so it
         outranks .list-group-item+.list-group-item. The selected spine is
         still 3px; its padding gives back the 2px so the text does not move. */
      '#ptLibraryBody .pt-template{border-radius:10px;margin-bottom:.6rem;border:1px solid #e7e3ee;',
      '  transition:background .15s,border-color .15s,box-shadow .15s;}',
      '#ptLibraryBody .pt-template:hover{border-color:#ddd6e2;}',
      '#ptLibraryBody .pt-template.is-selected{border-color:#e6d9b8;border-inline-start:3px solid #c9a84c;',
      '  padding-inline-start:calc(1rem - 2px);}',
      '.pt-template:hover{background:#faf9fc;border-color:#ddd6e2;}',
      /* The selected state. No white-on-blue, no colour the rest of the
         product does not use. */
      '.pt-template.is-selected{background:#fbf7ee;border-color:#e6d9b8;',
      '  border-inline-start-color:#c9a84c;box-shadow:0 1px 6px rgba(22,36,63,.07);}',
      '.pt-template.is-selected:hover{background:#faf5ea;}',
      /* Bootstrap's own selected panel, disowned explicitly: the class is still
         toggled by nothing here, but a stray `.active` from anywhere else must
         not repaint the row blue. */
      '.pt-template.active{background:#fbf7ee;color:inherit;border-color:#e6d9b8;}',
      /* Task #799: the marker is a real control now - a plus on every card
         that turns into a check when that card is chosen (and the name box
         opens), and back into a plus when it is let go. It used to be
         invisible until selected, so nothing on a card said it could be
         used to start an event. */
      '.pt-tick{width:1.75rem;height:1.75rem;border-radius:50%;display:inline-flex;position:relative;',
      '  align-items:center;justify-content:center;background:#fff;color:#8a6d2f;padding:0;',
      '  border:1px solid #d9c89b;font-size:.85rem;flex:0 0 auto;cursor:pointer;',
      '  transition:background .16s ease,color .16s ease,border-color .16s ease;}',
      '.pt-tick:hover{background:#fbf7ee;}',
      '.pt-tick:focus-visible{outline:2px solid #c9a84c;outline-offset:2px;}',
      '.pt-tick .bi{position:absolute;transition:transform .18s ease,opacity .18s ease;}',
      '.pt-tick .pt-tick-check{transform:scale(.4) rotate(-90deg);opacity:0;}',
      '.pt-template.is-selected .pt-tick{background:#c9a84c;border-color:#c9a84c;color:#fff;}',
      '.pt-template.is-selected .pt-tick .pt-tick-plus{transform:scale(.4) rotate(90deg);opacity:0;}',
      '.pt-template.is-selected .pt-tick .pt-tick-check{transform:none;opacity:1;}',
      /* The name-and-create row, once it has been moved inside the selected
         card: it reads as the bottom half of that card rather than as a
         separate thing at the end of the list. */
      '#ptCreateRow.pt-create-inline{margin-top:.75rem;padding-top:.75rem;',
      '  border-top:1px dashed #e6d9b8;}',
      '#ptCreateRow.pt-create-inline .form-text{margin-bottom:0;}',
      '.pt-create-bar{display:flex;align-items:center;gap:.5rem 1rem;flex-wrap:wrap;}',
      '.pt-create-help{margin:0;flex:1 1 16rem;}',
      '#ptCreateRow:not(.pt-has-messages) .pt-create-help{display:none;}',
      '#ptCreateRow.pt-create-inline #ptCancelCreate{display:none;}',
      '.pt-template.is-selected .pt-actions .btn{background:transparent;}',
      /* The buttons in this dialog were Bootstrap blue too — the same colour
         the task is about, on the action it is about. The studio already
         restyles its own primaries to the brand navy; this does it for the
         dialog, which runs on my-projects in seven languages as well. */
      /* Targeted by the ELEMENT's own id and by the list's, not by whichever
         wrapper it happens to be in: this dialog is shown inside a Bootstrap
         modal on my-projects and adopted into a panel on the planner studio,
         and a selector that names one of those silently misses the other. */
      '#ptCreateBtn{background:#16243F;border-color:#16243F;}',
      '#ptCreateBtn:hover:not(:disabled){background:#0d1524;border-color:#0d1524;}',
      /* Disabled reads as disabled, rather than as a paler blue. */
      '#ptCreateBtn:disabled{background:#f3f1f6;border-color:#ddd6e2;color:#9aa0ab;opacity:1;}',
      '#ptLibraryBody .btn-outline-primary{color:#16243F;border-color:#ddd6e2;background:#fff;}',
      '#ptLibraryBody .btn-outline-primary:hover{color:#16243F;border-color:#c9a84c;background:#fbf7ee;}',
      /* ...and the focus ring on the name box, which was the third blue. */
      '#ptNewEventName:focus{border-color:#c9a84c;box-shadow:0 0 0 .2rem rgba(201,168,76,.22);}',
      /* Task #802: the fill-in step as label | box rows. The label column fits
         the longest short label; the box is sized for a name or a place, not
         the width of the page. Stacks back on a phone. */
      '.pt-fill-row{display:grid;grid-template-columns:minmax(8rem,12rem) minmax(0,20rem);gap:.35rem 1rem;align-items:center;margin-bottom:.45rem;}',
      '.pt-fill-row label{margin:0;overflow-wrap:anywhere;}',
      '.pt-fill-row .pt-fill-num{max-width:7rem;}',
      /* Task #797 (round 5): the languages to translate the new event into, as
         the builder's Translate-step cards (translations.css .unified-language-card)
         - code badge, name, native name, checkbox - in a scrolling grid. */
      '.pt-langs-search{max-width:20rem;}',
      '.pt-lang-scroll{max-height:15.5rem;overflow-y:auto;border:1px solid #e5e7eb;border-radius:.5rem;background:#f9fafb;padding:.5rem;}',
      '.pt-lang-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(12.5rem,1fr));gap:.5rem;}',
      '.pt-lang-card{display:flex;align-items:center;gap:.6rem;margin:0;padding:.5rem .6rem;background:#fff;border:2px solid #e5e7eb;border-radius:.5rem;cursor:pointer;user-select:none;transition:border-color .15s,box-shadow .15s;}',
      '.pt-lang-card:hover{border-color:#16243F;}',
      '.pt-lang-card:has(input:checked){border-color:#16243F;background:rgba(22,36,63,.05);box-shadow:0 3px 10px rgba(22,36,63,.12);}',
      '.pt-lang-code{display:inline-flex;align-items:center;justify-content:center;min-width:2.3rem;height:2.3rem;padding:0 .2rem;border-radius:.4rem;background:#16243F;color:#fff;font-weight:700;font-size:.78rem;flex-shrink:0;}',
      '.pt-lang-names{display:flex;flex-direction:column;min-width:0;flex:1;line-height:1.2;}',
      '.pt-lang-name{font-weight:600;font-size:.86rem;color:#111827;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}',
      '.pt-lang-native{font-size:.78rem;color:#6b7280;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}',
      '.pt-lang-card .form-check-input{margin:0;width:1.25rem;height:1.25rem;flex-shrink:0;border:2px solid #9ca3af;cursor:pointer;}',
      '.pt-lang-card .form-check-input:checked{background-color:#16243F;border-color:#16243F;}',
      /* Task #797 round 6: the builder's free-plan language banner (language-selector-ui.js). */
      '.pt-lang-limit:not(.d-none){display:flex;align-items:flex-start;gap:.5rem;margin-top:.5rem;padding:.6rem .85rem;background:linear-gradient(135deg,#f8f0ff,#f0e8ff);border:1px solid #d4b8ff;border-radius:.5rem;font-size:.82rem;color:#4a3070;}',
      '.pt-lang-limit-icon{font-size:1rem;line-height:1.2;}',
      /* Task #832: the folded "Look" section (header image, colour, envelope cards). */
      '.pt-look summary{cursor:pointer;}',
      '.pt-look-body{display:grid;gap:.75rem;margin-top:.6rem;padding:.75rem;border:1px solid #e5e7eb;border-radius:.5rem;background:#f9fafb;}',
      '.pt-look-slot{display:flex;align-items:center;gap:.65rem;margin-bottom:.4rem;}',
      '.pt-look-thumb{width:3.25rem;height:3.25rem;border-radius:.4rem;object-fit:cover;border:1px solid #e5e7eb;background:#fff;flex-shrink:0;}',
      '.pt-look-empty{display:inline-flex;align-items:center;justify-content:center;color:#9ca3af;font-size:1.2rem;}',
      '.pt-swatch{width:1.75rem;height:1.75rem;border-radius:50%;border:2px solid #fff;box-shadow:0 0 0 1px #d1d5db;padding:0;cursor:pointer;}',
      '.pt-swatch.is-on{box-shadow:0 0 0 2px #16243F;}',
      /* #832 round 2: the builder's round "+" chip (builder-palette.js) - a
         rainbow ring with a drawn plus; the colour input sits invisibly on top. */
      '.pt-swatch-plus{position:relative;display:inline-flex;align-items:center;justify-content:center;box-sizing:border-box;width:1.75rem;height:1.75rem;border-radius:50%;border:2px solid #fff;box-shadow:0 0 0 1px #d1d5db;overflow:hidden;cursor:pointer;background-clip:padding-box;background-image:radial-gradient(circle,#fff 0 44%,transparent 45%),conic-gradient(#e5534b,#e3a83c,#d8c93f,#5aa95a,#4a9bd1,#7b6fd0,#c968b0,#e5534b);}',
      '.pt-swatch-plus input[type="color"]{position:absolute;top:0;left:0;width:100%;height:100%;min-width:0;min-height:0;margin:0;padding:0;border:0;opacity:0;cursor:pointer;background:transparent;}',
      '.pt-plus{position:relative;display:block;width:9px;height:9px;pointer-events:none;font-size:0;line-height:0;color:transparent;}',
      '.pt-plus::before,.pt-plus::after{content:"";position:absolute;background:#4b5563;border-radius:1px;}',
      '.pt-plus::before{left:0;right:0;top:50%;height:2px;transform:translateY(-50%);}',
      '.pt-plus::after{top:0;bottom:0;left:50%;width:2px;transform:translateX(-50%);}',
      '@media (max-width:575.98px){.pt-fill-row{grid-template-columns:1fr;}.pt-lang-grid{grid-template-columns:1fr 1fr;}.pt-lang-code{min-width:2rem;height:2rem;}}',
      /* Task #801: the library intro is hidden while details are filled in. */
      '.pt-filling .pw-pane-lead{display:none;}'
    ].join('');
    var el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = css;
    document.head.appendChild(el);
  }

  function inject() {
    injectStyles();
    if (document.getElementById('plannerBrandingModal')) return;
    var host = document.createElement('div');
    host.id = 'plannerModalsHost';
    host.innerHTML = markup();
    document.body.appendChild(host);
  }

  window.PlannerModals = {
    t: t,
    inject: inject,
    lang: lang,
    chargeLabel: chargeLabel,
    monthLabel: monthLabel
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', inject);
  } else {
    inject();
  }
})();
